import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getLoyaltyMetrics } from '@/api/clarity'
import { ScalrTableColumn } from '@/components/customComponents'
import { chartColors } from '@/constants/COLOR_PALETTE'
import { formatMetric } from '@/hooks/clarity/util'
import {
  FreqChartRecord,
  FrequencyBracketMetrics,
  FrequencyBracketsMetrics,
  LoyaltyBranchModel,
  LoyaltyChartModel,
  LoyaltyPersonaLeaderboardModel,
  MetricCardComparisonModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'
import { EChartsOption } from 'echarts'

const COMPANY_SYSTEM = 'Branch Total'

const personaLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Persona', accessorKey: 'personaName', type: 'text' },
  { header: 'Sales %', accessorKey: 'salesPercentage', type: 'percentage' },
  {
    header: 'Weekly Visit Frequency',
    accessorKey: 'weeklyVisitFrequency',
    type: 'number',
  },
  { header: 'AOV', accessorKey: 'aov', type: 'money' },
  {
    header: 'Retention %',
    accessorKey: 'retentionPercentage',
    type: 'percentage',
  },
  {
    header: 'Discount %',
    accessorKey: 'discountPenetrationPercentage',
    type: 'percentage',
  },
]

export default function useLoyaltyMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<LoyaltyBranchModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<LoyaltyBranchModel[]>(
    []
  )
  const [companyData, setCompanyData] = useState<LoyaltyBranchModel>()
  const [companyTrendData, setCompanyTrendData] = useState<LoyaltyBranchModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<LoyaltyBranchModel>>()
  const [chartData, setChartData] = useState<LoyaltyChartModel>()
  const [personaData, setPersonaData] =
    useState<LoyaltyPersonaLeaderboardModel[]>()

  const [frequencyChart7DaysData, setFrequencyChart7DaysData] =
    useState<EChartsOption>()
  const [frequencyChart30DaysData, setFrequencyChart30DaysData] =
    useState<EChartsOption>()
  const [churnRateChartData, setChurnRateChartData] = useState<EChartsOption>()
  const [orderCountBracketsChartData, setOrderCountBracketsChartData] =
    useState<EChartsOption>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics, companyMetrics, chartData, personaMetrics } =
          await fetchLoyaltyMetrics(branches, startDate, endDate)
        setLocalTrendingData(branches, setMetricCardData)

        const frequencyChart7DaysData = getFrequencyChart7DaysData(
          chartData?.frequencyChart.sevenDays
        )
        const frequencyChart30DaysData = getFrequencyChart30DaysData(
          chartData?.frequencyChart.thirtyDays
        )

        const churnRateChartData = getChurnRateChartData({
          churnPercentage: chartData?.churnChart.churnPercentage,
          reactivationPercentage: chartData?.churnChart.reactivationPercentage,
          avgDaysToReactivate: chartData?.churnChart.avgDaysToReactivate,
        })

        const orderCountBracketsChartData = getOrderCountBracketsChartData(
          companyMetrics?.frequencyBrackets
        )

        setBranchData(branchMetrics)
        setCompanyData(companyMetrics)
        setChartData(chartData)
        setPersonaData(personaMetrics)
        setFrequencyChart7DaysData(frequencyChart7DaysData)
        setFrequencyChart30DaysData(frequencyChart30DaysData)
        setChurnRateChartData(churnRateChartData)
        setOrderCountBracketsChartData(orderCountBracketsChartData)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics, companyMetrics } = await fetchLoyaltyMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )

        setBranchTrendData(branchMetrics)
        setCompanyTrendData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching loyalty metrics data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    branchData,
    companyData,
    branchTrendData,
    companyTrendData,
    metricCardData,
    chartData,
    personaData,
    personaLeaderboardColumns,
    frequencyChart7DaysData,
    frequencyChart30DaysData,
    churnRateChartData,
    orderCountBracketsChartData,
    loading,
    error,
    refetch,
  }
}

// I have carried it to a util file. Try with that and change others
async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<LoyaltyBranchModel>
  ) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const { companyMetrics: mCompanyMetrics } = await fetchLoyaltyMetrics(
    branches,
    dateToString(mtdDate, 'from'),
    dateToString(mtdDate, 'to')
  )

  const { companyMetrics: mCompanyMetricsTrending } = await fetchLoyaltyMetrics(
    branches,
    dateToString(mtdTrendingDate, 'from'),
    dateToString(mtdTrendingDate, 'to')
  )

  const { companyMetrics: yCompanyMetrics } = await fetchLoyaltyMetrics(
    branches,
    dateToString(ytdDate, 'from'),
    dateToString(ytdDate, 'to')
  )

  const { companyMetrics: yCompanyMetricsTrending } = await fetchLoyaltyMetrics(
    branches,
    dateToString(ytdTrendingDate, 'from'),
    dateToString(ytdTrendingDate, 'to')
  )

  setMetricCardData({
    currMetricsMonthly: mCompanyMetrics,
    currMetricsYearly: yCompanyMetrics,
    prevMetricsMonthly: mCompanyMetricsTrending,
    prevMetricsYearly: yCompanyMetricsTrending,
  })
}

async function fetchLoyaltyMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  branchMetrics: LoyaltyBranchModel[]
  companyMetrics: LoyaltyBranchModel
  chartData: LoyaltyChartModel
  personaMetrics: LoyaltyPersonaLeaderboardModel[]
}> {
  try {
    const response = await getLoyaltyMetrics(branches, startDate, endDate)

    const metrics: LoyaltyBranchModel[] = response.branch_data.map(
      (responseMetrics: any) => {
        return setBranchMetrics(
          responseMetrics.branch_name,
          responseMetrics.loyalty_percentage,
          responseMetrics.new_user_percentage,
          responseMetrics.discount_penetration_percentage,
          responseMetrics.avg_frequency_7day,
          responseMetrics.avg_frequency_30day,
          responseMetrics.retention_percentage,
          responseMetrics.frequency_brackets
        )
      }
    )

    const chartData: LoyaltyChartModel = setChartMetrics(
      response.chart_data.frequency_chart['7_days'],
      response.chart_data.frequency_chart['30_days'],
      response.chart_data.churn_chart.churn_percentage,
      response.chart_data.churn_chart.reactivation_percentage,
      response.chart_data.churn_chart.avg_days_to_reactivate
    )

    const personaMetrics: LoyaltyPersonaLeaderboardModel[] =
      response.persona_leaderboard.map((responseMetrics: any) => {
        return setPersonaMetrics(
          responseMetrics.persona_name,
          responseMetrics.sales_percentage,
          responseMetrics.weekly_visit_frequency,
          responseMetrics.aov,
          responseMetrics.retention_percentage,
          responseMetrics.discount_penetration_percentage
        )
      })

    const branchMetrics = metrics.filter(
      (metric) => metric.stand !== COMPANY_SYSTEM
    )
    const companyMetrics = metrics.filter(
      (metric) => metric.stand === COMPANY_SYSTEM
    )

    return {
      branchMetrics,
      companyMetrics: companyMetrics[0],
      chartData,
      personaMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setBranchMetrics(
  stand: string,
  loyaltyPercentage: number,
  newUserPercentage: number,
  discountPenetrationPercentage: number,
  avgFrequency7day: number,
  avgFrequency30day: number,
  retentionPercentage: number,
  frequencyBrackets: FrequencyBracketsMetrics
): LoyaltyBranchModel {
  const freqBrackets: FrequencyBracketsMetrics = {
    no_loyalty: {
      percentage: formatMetric(frequencyBrackets.no_loyalty.percentage, 2),
    } as FrequencyBracketMetrics,
    '1_order': {
      percentage: formatMetric(frequencyBrackets['1_order'].percentage, 2),
    } as FrequencyBracketMetrics,
    '2_orders': {
      percentage: formatMetric(frequencyBrackets['2_orders'].percentage, 2),
    } as FrequencyBracketMetrics,
    '3_5_orders': {
      percentage: formatMetric(frequencyBrackets['3_5_orders'].percentage, 2),
    } as FrequencyBracketMetrics,
    '6_9_orders': {
      percentage: formatMetric(frequencyBrackets['6_9_orders'].percentage, 2),
    } as FrequencyBracketMetrics,
    '10_plus_orders': {
      percentage: formatMetric(
        frequencyBrackets['10_plus_orders'].percentage,
        2
      ),
    } as FrequencyBracketMetrics,
  }

  return {
    stand: stand === 'system_total' ? 'Branch Total' : stand,
    loyaltyPercentage: formatMetric(loyaltyPercentage, 2),
    newUserPercentage: formatMetric(newUserPercentage, 2),
    discountPenetrationPercentage: formatMetric(
      discountPenetrationPercentage,
      2
    ),
    frequencyBrackets: freqBrackets,
    avgFrequency7day: formatMetric(avgFrequency7day, 2),
    avgFrequency30day: formatMetric(avgFrequency30day, 2),
    retentionPercentage: formatMetric(retentionPercentage, 2),
  }
}

function setChartMetrics(
  sevenDays: FreqChartRecord,
  thirtyDays: FreqChartRecord,
  churnPercentage: number,
  reactivationPercentage: number,
  avgDaysToReactivate: number
): LoyaltyChartModel {
  const formattedSevenDays: FreqChartRecord = Object.entries(sevenDays).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: formatMetric(value, 2),
    }),
    {}
  )

  const formattedThirtyDays: FreqChartRecord = Object.entries(
    thirtyDays
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: formatMetric(value, 2),
    }),
    {}
  )

  return {
    frequencyChart: {
      sevenDays: formattedSevenDays,
      thirtyDays: formattedThirtyDays,
    },
    churnChart: {
      churnPercentage: formatMetric(churnPercentage, 2),
      reactivationPercentage: formatMetric(reactivationPercentage, 2),
      avgDaysToReactivate: formatMetric(avgDaysToReactivate, 0),
    },
  }
}

function setPersonaMetrics(
  personaName: string,
  salesPercentage: number,
  weeklyVisitFrequency: number,
  aov: number,
  retentionPercentage: number,
  discountPenetrationPercentage: number
): LoyaltyPersonaLeaderboardModel {
  return {
    personaName,
    salesPercentage: formatMetric(salesPercentage, 2),
    weeklyVisitFrequency: formatMetric(weeklyVisitFrequency, 2),
    aov: formatMetric(aov, 2),
    retentionPercentage: formatMetric(retentionPercentage, 2),
    discountPenetrationPercentage: formatMetric(
      discountPenetrationPercentage,
      2
    ),
  }
}

const getFrequencyChart7DaysData = (
  sevenDays: FreqChartRecord
): EChartsOption => {
  const sortedData = Object.entries(sevenDays)
    .sort(([, a], [, b]) => a - b)
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as FreqChartRecord)

  const branches = Object.keys(sortedData)
  const data = Object.values(sortedData)

  return {
    legend: {
      data: ['7 days'],
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      icon: 'circle',
      itemGap: 20,
    },
    grid: {
      left: '3%',
      right: '15%',
      bottom: '5%',
      top: '10%',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: {
      show: false,
      type: 'value',
    },
    yAxis: {
      type: 'category',
      data: branches,
      axisLabel: {
        show: true,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series: [
      {
        name: '7 days',
        type: 'bar',
        data,
        barWidth: '70%',
        itemStyle: {
          color: chartColors[0],
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#fff',
        },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#333',
          fontWeight: 'bold',
        },
      },
    ],
    center: ['50%', '60%'],
  }
}

const getFrequencyChart30DaysData = (
  thirtyDays: FreqChartRecord
): EChartsOption => {
  const sortedData = Object.entries(thirtyDays)
    .sort(([, a], [, b]) => a - b)
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as FreqChartRecord)

  const branches = Object.keys(sortedData)
  const data = Object.values(sortedData)

  return {
    legend: {
      data: ['30 days'],
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      icon: 'circle',
      itemGap: 20,
    },
    tooltip: {
      trigger: 'axis',
    },
    grid: {
      left: '3%',
      right: '15%',
      bottom: '5%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      show: false,
      type: 'value',
    },
    yAxis: {
      type: 'category',
      data: branches,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series: [
      {
        name: '30 days',
        type: 'bar',
        data,
        barWidth: '70%',
        itemStyle: {
          color: chartColors[1],
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#fff',
        },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#333',
          fontWeight: 'bold',
        },
      },
    ],
    center: ['50%', '60%'],
  }
}

const getChurnRateChartData = ({
  churnPercentage,
  reactivationPercentage,
  avgDaysToReactivate,
}: {
  churnPercentage: number
  reactivationPercentage: number
  avgDaysToReactivate: number
}): EChartsOption => {
  return {
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: [
        '30-day life time churn',
        'Reactivation % of customers that churned',
      ],
      axisLabel: {
        interval: 0,
        rotate: 0,
        width: 80,
        overflow: 'break',
        lineHeight: 14,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      show: false,
      splitLine: {
        show: false,
      },
    },
    series: [
      {
        type: 'bar',
        data: [
          {
            value: churnPercentage,
            itemStyle: {
              color: chartColors[11],
              borderRadius: 6,
              borderWidth: 1,
              borderColor: '#fff',
            },
          },
          {
            value: reactivationPercentage,
            itemStyle: {
              color: chartColors[14],
              borderRadius: 6,
              borderWidth: 1,
              borderColor: '#fff',
            },
          },
        ],
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%',
          fontSize: 14,
          fontWeight: 'bold',
        },
        barWidth: '30%',
      },
      {
        type: 'line',
        markLine: {
          symbol: 'none',
          label: {
            formatter: `- ${avgDaysToReactivate} days -`,
            position: 'middle',
            fontSize: 12,
            color: '#111',
            fontWeight: 'bold',
          },
          lineStyle: {
            color: 'transparent',
          },
          data: [{ yAxis: avgDaysToReactivate / 3 }],
        },
      },
    ],
  }
}

const getOrderCountBracketsChartData = (
  frequencyBrackets: FrequencyBracketsMetrics
): EChartsOption => {
  const brackets = [
    frequencyBrackets['10_plus_orders'].percentage,
    frequencyBrackets['6_9_orders'].percentage,
    frequencyBrackets['3_5_orders'].percentage,
    frequencyBrackets['2_orders'].percentage,
    frequencyBrackets['1_order'].percentage,
    frequencyBrackets.no_loyalty.percentage,
  ]

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '3%',
      right: '15%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'category',
      data: ['10+', '6 to 9', '3 to 5', '2', '1', 'No Loyalty'],
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series: [
      {
        type: 'bar',
        data: brackets,
        barWidth: '70%',
        itemStyle: {
          color: chartColors[0],
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#fff',
        },
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          fontSize: 14,
          fontWeight: 'normal',
        },
      },
    ],
  }
}
